import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Briefcase, Calendar, MapPin, Search } from 'lucide-react'
import {
  getMyApplications, WORK_TYPE_LABELS,
  type ApiJobApplication,
} from '@/services/jobService'
import CompanyLogo from '@/components/ui/CompanyLogo'

// หน้า "ใบสมัครของฉัน" (ผู้สมัคร) — เห็นสถานะที่บริษัทเปลี่ยนแบบ real-time (ไม่เห็น company_note/company_saved
// ซึ่งเป็นข้อมูลภายในของบริษัท — คนละ schema กับที่บริษัทเห็น ดู CompanyApplicationSchema ฝั่ง backend)

const STATUS_META: Record<string, { label: string; pill: string }> = {
  APPLIED: { label: 'รอการพิจารณา', pill: 'bg-accent/10 text-accent' },
  REVIEWED: { label: 'เปิดดูแล้ว', pill: 'bg-[#F1F0F7] text-[#69738A]' },
  INTERVIEWED: { label: 'นัดสัมภาษณ์', pill: 'bg-amber-50 text-amber-700' },
  OFFERED: { label: 'ผ่านการคัดเลือก', pill: 'bg-emerald-50 text-emerald-700' },
  REJECTED: { label: 'ไม่ผ่านการคัดเลือก', pill: 'bg-rose-50 text-rose-600' },
  WITHDRAWN: { label: 'ถอนใบสมัคร', pill: 'bg-rose-50 text-rose-600' },
}

const FILTER_TABS = [
  { key: 'ALL', label: 'ทั้งหมด' },
  { key: 'APPLIED', label: 'รอการพิจารณา' },
  { key: 'INTERVIEWED', label: 'นัดสัมภาษณ์' },
  { key: 'OFFERED', label: 'ผ่านการคัดเลือก' },
  { key: 'REJECTED', label: 'ไม่ผ่าน' },
] as const

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' })

export default function MyApplicationsTab() {
  const [applications, setApplications] = useState<ApiJobApplication[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [filter, setFilter] = useState<typeof FILTER_TABS[number]['key']>('ALL')
  const [search, setSearch] = useState('')

  useEffect(() => {
    getMyApplications()
      .then(setApplications)
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'โหลดใบสมัครไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    let list = applications
    if (filter !== 'ALL') list = list.filter((a) => a.status === filter)
    const q = search.trim().toLowerCase()
    if (q) list = list.filter((a) => a.job_title.toLowerCase().includes(q) || (a.company_name ?? '').toLowerCase().includes(q))
    return [...list].sort((a, b) => new Date(b.applied_at).getTime() - new Date(a.applied_at).getTime())
  }, [applications, filter, search])

  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: applications.length }
    for (const a of applications) c[a.status] = (c[a.status] ?? 0) + 1
    return c
  }, [applications])

  if (loading) {
    return <div className="flex items-center justify-center rounded-2xl border border-dashed border-[#E1DCF5] bg-[#FAFAFF] py-20 text-sm text-[#69738A]">กำลังโหลด...</div>
  }
  if (loadError) {
    return <div className="rounded-2xl border border-dashed border-rose-200 bg-rose-50 px-6 py-10 text-center text-sm text-rose-600">{loadError}</div>
  }

  return (
    <div>
      <div className="mb-5 flex gap-6 overflow-x-auto border-b border-[#E7E7ED]">
        {FILTER_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            className={`shrink-0 border-b-2 pb-3 text-sm font-medium transition-colors ${
              filter === t.key ? 'border-accent text-accent' : 'border-transparent text-[#69738A] hover:text-[#19172A]'
            }`}
          >
            {t.label} <span className="text-xs text-[#69738A]">({counts[t.key] ?? 0})</span>
          </button>
        ))}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#69738A]" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ค้นหาตำแหน่งงาน, บริษัท..."
          className="w-full rounded-lg border border-[#E7E5FF] bg-white py-2 pl-9 pr-3 text-sm text-[#19172A] outline-none focus:border-accent"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E1DCF5] bg-[#FAFAFF] px-6 py-16 text-center">
          <Briefcase className="mx-auto mb-3 h-8 w-8 text-[#9691AC]" />
          <p className="text-sm text-[#69738A]">{applications.length === 0 ? 'คุณยังไม่ได้สมัครงานใด' : 'ไม่พบใบสมัครที่ตรงกับเงื่อนไข'}</p>
          {applications.length === 0 && (
            <Link to="/jobs" className="mt-3 inline-block text-sm font-medium text-accent hover:underline">ดูตำแหน่งงานที่เปิดรับ</Link>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((a) => {
            const meta = STATUS_META[a.status] ?? { label: a.status, pill: 'bg-[#F1F0F7] text-[#69738A]' }
            const jobClosed = a.job_status !== 'ACTIVE'
            return (
              <li key={a.application_id} className="rounded-2xl border border-[#E7E5FF] bg-white p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <CompanyLogo name={a.company_name || a.job_title} logoUrl={a.company_logo_url} size={44} className="rounded-full" initialsCount={1} />
                    <div>
                      <p className="text-[15px] font-semibold text-[#19172A]">{a.job_title}</p>
                      <p className="text-sm text-[#69738A]">{a.company_name || '-'}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#9691AC]">
                        <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {a.location}</span>
                        <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {WORK_TYPE_LABELS[a.work_type] || a.work_type}</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> สมัครเมื่อ {formatDate(a.applied_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${meta.pill}`}>{meta.label}</span>
                    {jobClosed && <span className="text-[11px] text-[#9691AC]">ตำแหน่งนี้ปิดรับสมัครแล้ว</span>}
                  </div>
                </div>
                {!jobClosed && (
                  <div className="mt-3 border-t border-[#F0EEFB] pt-3">
                    <Link to={`/jobs/${a.job_id}`} className="text-xs font-medium text-accent hover:underline">ดูประกาศงาน</Link>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
